import { BookOpen, MapPin } from "lucide-react";
import {
	companyCanonicalUrl,
	companyFromMatches,
	companyNotFoundMeta,
	companySocialMeta,
	fetchCompanySection,
	parseIcoSlug,
} from "~/lib/company-profile";
import { envConfig } from "~/lib/env-validation";
import { formatDate } from "~/lib/format";
import { generateMetaTags } from "~/lib/seo";
import type { TradeLicense, TradeLicensesResponse } from "~/types/company";
import type { Route } from "./+types/zivnosti";

// --- Loader ---

export async function loader({ params }: Route.LoaderArgs) {
	const ico = parseIcoSlug(params.icoSlug);

	// Tolerate 404/network errors — the endpoint ships with Wave 2 data; until
	// then (and for companies without RŽP records) the page keeps its empty state.
	const tradeLicenses = await fetchCompanySection<TradeLicensesResponse>(
		`${envConfig.apiBaseUrl}/companies/${ico}/trade-licenses`,
	).catch(() => null);

	return { tradeLicenses };
}

// --- Meta ---

export function meta({ data: loaderData, matches }: Route.MetaArgs) {
	const company = companyFromMatches(matches);
	if (!company) return companyNotFoundMeta();

	return generateMetaTags({
		...companySocialMeta(company),
		title: `Živnostenský rejstřík – ${company.name} | FirmIndex`,
		description: `Živnosti a obory činnosti firmy ${company.name} (IČO: ${company.ico}) ze živnostenského rejstříku.`,
		canonical: companyCanonicalUrl(company, "zivnosti"),
		noindex: !loaderData?.tradeLicenses || loaderData.tradeLicenses.licenses.length === 0,
	});
}

// --- Presentation ---

const LICENSE_TYPE_LABELS: Record<string, string> = {
	FREE: "Ohlašovací volná",
	CRAFT: "Ohlašovací řemeslná",
	BOUND: "Ohlašovací vázaná",
	CONCESSION: "Koncesovaná",
};

/** API sends raw TradeLicenseStatus / BusinessPremisesStatus enum values —
 * map them to Czech labels + status-appropriate badge tones. Unknown values
 * fall back to a neutral badge with the raw text. */
const LICENSE_STATUS_BADGES: Record<string, { label: string; className: string }> = {
	ACTIVE: { label: "Aktivní", className: "badge badge-soft badge-success" },
	SUSPENDED: { label: "Pozastavena", className: "badge badge-soft badge-warning" },
	TERMINATED: { label: "Zaniklá", className: "badge badge-soft badge-error" },
};

const PREMISES_STATUS_BADGES: Record<string, { label: string; className: string }> = {
	ACTIVE: { label: "Aktivní", className: "badge badge-soft badge-sm badge-success" },
	SUSPENDED: { label: "Pozastavena", className: "badge badge-soft badge-sm badge-warning" },
	CLOSED: { label: "Uzavřena", className: "badge badge-soft badge-sm badge-error" },
};

function LicenseCard({ license }: { license: TradeLicense }) {
	const typeLabel = license.licenseType ? LICENSE_TYPE_LABELS[license.licenseType] : null;
	const statusBadge = license.status
		? (LICENSE_STATUS_BADGES[license.status] ?? {
				label: license.status,
				className: "badge badge-soft",
			})
		: null;

	return (
		<div className="card-surface rounded-xl p-6">
			<div className="flex items-start justify-between gap-3 mb-4">
				<h2 className="text-lg font-semibold">{license.subject}</h2>
				{statusBadge && (
					<span className={`${statusBadge.className} shrink-0`}>{statusBadge.label}</span>
				)}
			</div>

			<dl className="flex flex-col gap-3 text-sm">
				{typeLabel && <DataRow label="Druh živnosti" value={typeLabel} />}
				{license.validFrom && (
					<DataRow label="Vznik oprávnění" value={formatDate(license.validFrom)} />
				)}
				<DataRow
					label="Platnost"
					value={license.validTo ? `do ${formatDate(license.validTo)}` : "na dobu neurčitou"}
				/>
			</dl>

			{license.activityFields.length > 0 && (
				<div className="mt-4 border-t border-gray-100 pt-4">
					<p className="text-xs font-medium text-muted-foreground mb-2">Obory činnosti</p>
					<ul className="flex flex-col gap-1.5 text-sm">
						{license.activityFields.map((field) => (
							<li key={field.name} className="flex items-baseline gap-2">
								<span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-gray-400" aria-hidden />
								<span>
									{field.name}
									{field.startedAt && (
										<span className="text-xs text-muted-foreground">
											{" "}
											· Zahájeno {formatDate(field.startedAt)}
										</span>
									)}
								</span>
							</li>
						))}
					</ul>
				</div>
			)}
		</div>
	);
}

function DataRow({ label, value }: { label: string; value: string }) {
	return (
		<div className="flex items-baseline gap-2">
			<dt className="text-muted-foreground w-40 shrink-0">{label}</dt>
			<dd>{value}</dd>
		</div>
	);
}

// --- Component ---

export default function CompanyZivnostiRoute({ loaderData }: Route.ComponentProps) {
	const data = loaderData.tradeLicenses;

	if (!data || data.licenses.length === 0) {
		return (
			<div className="py-8 text-center">
				<BookOpen className="mx-auto h-10 w-10 text-muted-foreground mb-3" />
				<p className="text-sm font-medium text-muted-foreground">
					Údaje ze živnostenského rejstříku připravujeme.
				</p>
				<p className="text-xs text-muted-foreground mt-1">
					Živnosti, obory činnosti a provozovny zde zobrazíme, jakmile data zpracujeme.
				</p>
			</div>
		);
	}

	return (
		<div className="flex flex-col gap-4 pt-4">
			{data.licenses.map((license) => (
				<LicenseCard key={license.id} license={license} />
			))}

			{data.premises.length > 0 && (
				<div className="card-surface rounded-xl p-6">
					<div className="flex items-center gap-2 mb-4">
						<div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary-50">
							<MapPin className="h-4 w-4 text-primary-600" />
						</div>
						<h2 className="text-lg font-semibold">Provozovny</h2>
					</div>
					<ul className="flex flex-col gap-2">
						{data.premises.map((premises) => {
							const statusBadge = premises.status
								? (PREMISES_STATUS_BADGES[premises.status] ?? {
										label: premises.status,
										className: "badge badge-soft badge-sm",
									})
								: null;
							return (
								<li
									key={premises.id}
									className="flex items-center justify-between gap-3 rounded-lg border border-gray-100 p-3 text-sm"
								>
									<span>{premises.address ?? "Adresa neuvedena"}</span>
									{statusBadge && (
										<span className={`${statusBadge.className} shrink-0`}>{statusBadge.label}</span>
									)}
								</li>
							);
						})}
					</ul>
				</div>
			)}
		</div>
	);
}
